import { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import {
  Loading,
  Flex,
  FlexColumn,
  Breadcrumbs,
  EmptyStateBox,
} from '@elementary/common-ui/components';
import { AI_USAGE } from '@elementary/common-ui/constants/queryKeys';
import * as api from 'shared/services/settingsApi';
import MonthlyCreditsCard from './MonthlyCreditsCard';
import AiSpendByDayChart from './AiSpendByDayChart';
import UsageCard from './UsageCard';
import RunTypeFilter from './RunTypeFilter';
import RangeToggle from './RangeToggle';
import { VisibleRunTypes } from './types';
import { useAiUsageViewModel } from './useAiUsageViewModel';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  gap: 24px;
`;

const UsageBreadcrumbs = () => (
  <Breadcrumbs items={['Account', 'AI Usage']} showTreeButton={false} />
);

const AiUsage = () => {
  const [range, setRange] = useState<api.SpendRange>('3m');
  const [visibleTypes, setVisibleTypes] = useState<VisibleRunTypes>({
    agent: true,
    ci: true,
  });

  const { data, isLoading, isError } = useQuery(
    [AI_USAGE, range],
    () => api.fetchAiUsage(range),
    { keepPreviousData: true },
  );

  const viewModel = useAiUsageViewModel(data);

  if (isLoading) {
    return (
      <Wrapper>
        <UsageBreadcrumbs />
        <Loading />
      </Wrapper>
    );
  }
  if (isError || !data) {
    return (
      <Wrapper>
        <UsageBreadcrumbs />
        <EmptyStateBox title="Failed to load AI usage">
          Something went wrong while loading AI usage. Please try again.
        </EmptyStateBox>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <UsageBreadcrumbs />
      <MonthlyCreditsCard
        creditsUsed={data.credits_used}
        creditLimit={data.credit_limit}
        resetsAt={data.resets_at}
      />
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <RunTypeFilter value={visibleTypes} onChange={setVisibleTypes} />
        <RangeToggle value={range} onChange={setRange} />
      </Flex>
      <AiSpendByDayChart data={viewModel.spend} visibleTypes={visibleTypes} />
      <UsageCard
        leaderboard={viewModel.leaderboard}
        sessions={viewModel.sessions}
      />
    </Wrapper>
  );
};

export default AiUsage;
